import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { FolderIcon } from "@common/folderIcon";
import { Safeguard } from "@scenes/safeguard";

import { ScrollingText } from "@common/scrollingText";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { useToggle } from "@common/treeToggle";
import { ProjectPopup, ToggleIcon } from "@scenes/projectPopup";
import { ListToggleArrow } from "@common/listToggleArrow";
import { RecordList } from "../../recordList";
import { Props } from "./types";
import styles from "./styles.scss";

function List({
  list,
  project,
  resource,
  depth: d,
  isRoot,
  areDetailsDisplayed,
  isReloading,
  showCompletionColumn,
  resourceRemoved,
}: Props) {
  const depth = d || 0;
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { isOpen, toggle } = useToggle(list.uuid, list.depth + 1, !isRoot);

  return (
    <div className={styles.list}>
      <div
        className={classNames(styles.main, {
          [styles.mainRoot]: isRoot,
        })}
      >
        <div
          className={styles.header}
          style={{ paddingLeft: `${depth === 0 ? 4 : depth * 3 + 4}rem` }}
        >
          <div className={styles.iconBox} onClick={toggle}>
            <ListToggleArrow isOpen={isOpen} />
            {isRoot && (
              <FolderIcon className={styles.folderIcon} isOpen={isOpen} />
            )}
          </div>
          <Safeguard useCases={[UseCases.editProject]} context={{ project }}>
            {({ canEditProject }) => (
              <>
                <ScrollingText speed={100}>
                  <p
                    className={`${
                      isRoot && project.memberDeactivated
                        ? styles.deactivated
                        : styles.name
                    }`}
                    style={resourceRemoved ? { cursor: "default" } : {}}
                    onClick={
                      resourceRemoved
                        ? undefined
                        : () =>
                            isRoot
                              ? canEditProject &&
                                pushView({
                                  key: UseCases.retrieveProject,
                                  data: { project, isMultiProject: true },
                                })
                              : pushView({
                                  key: UseCases.retrieveList,
                                  data: {
                                    listId: list.uuid,
                                    isMultiProject: true,
                                  },
                                })
                    }
                  >
                    {list.name}
                  </p>
                </ScrollingText>
                {resourceRemoved && (
                  <span className={styles.removedBadge}>
                    {t("timesheetPage.resourceRemoved")}
                  </span>
                )}
                {isRoot && canEditProject && !resourceRemoved && (
                  <VisibilitySwitch
                    renderChildren={(isVisible, show, hide) => (
                      <div
                        className={styles.projectPopup}
                        onMouseEnter={show}
                        onMouseLeave={hide}
                      >
                        <ToggleIcon className={styles.popupIcon} />
                        {isVisible && (
                          <div className={styles.projectPopupMain}>
                            <ProjectPopup projectId={project.uuid} />
                          </div>
                        )}
                      </div>
                    )}
                  />
                )}
              </>
            )}
          </Safeguard>
        </div>
        <div className={styles.recordList}>
          <RecordList
            {...list}
            type="list"
            project={project}
            resource={resource}
            records={[]}
            allocatedHours={list.totalOfAllocatedHours}
            spentTime={list.totalOfSpentTime}
            spentTimeInWeek={list.totalOfSpentTimeInWeek}
            remainingTime={list.totalOfRemainingTime}
            areDetailsDisplayed={areDetailsDisplayed}
            isReloading={isReloading}
            showCompletionColumn={showCompletionColumn}
          />
        </div>
      </div>
    </div>
  );
}

export { List };
